import * as Sentry from '@sentry/browser';

/**
 * Captures an error to Sentry with context
 * @param {Error|any} error - The error to capture
 * @param {Object} context - Additional context about the error
 * @param {string} context.action - The action that failed
 * @param {string} context.component - The component where the error occurred
 * @param {Object} context.extra - Additional data to include
 * @param {Object} context.tags - Tags to add to the error
 */
export function captureError(error, context = {}) {
  const {
    action = 'unknown',
    component = 'unknown',
    extra = {},
    tags = {},
  } = context;

  // Ensure we have an Error object
  const errorObj = error instanceof Error 
    ? error 
    : new Error(String(error));

  // Capture to Sentry with context
  Sentry.captureException(errorObj, {
    extra: {
      action,
      component,
      ...extra,
    },
    tags: {
      errorSource: 'frontend',
      ...tags,
    },
  });

  // Also log to console for debugging
  console.error(`[${component}] Error in ${action}:`, errorObj, extra);
}

/**
 * Wraps an async function to automatically capture errors
 * @param {Function} fn - The async function to wrap
 * @param {Object} context - Context for error reporting
 * @returns {Function} - Wrapped function that captures errors
 */
export function withErrorHandling(fn, context = {}) {
  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      captureError(error, context);
      throw error; // Re-throw so callers can still handle it
    }
  };
}

/**
 * Safely parses JSON response, capturing errors if parsing fails
 * Note: This consumes the response body, so it can only be called once per response
 * @param {Response} response - The fetch response
 * @param {Object} context - Context for error reporting
 * @returns {Promise<Object>} - Parsed JSON or throws error
 */
export async function safeJsonParse(response, context = {}) {
  try {
    const text = await response.text();
    if (!text) {
      throw new Error('Empty response body');
    }
    return JSON.parse(text);
  } catch (error) {
    const parseError = new Error(
      `Failed to parse JSON response: ${error.message}. Status: ${response.status} ${response.statusText}`
    );
    captureError(parseError, {
      ...context,
      extra: {
        ...context.extra,
        status: response.status,
        statusText: response.statusText,
        url: response.url,
        originalError: error.message,
      },
    });
    throw parseError;
  }
}

/**
 * Clones a response so it can be read multiple times
 * @param {Response} response - The response to clone
 * @returns {Response} - Cloned response
 */
export function cloneResponse(response) {
  return response.clone();
}

